/**
 * StrategiesPlaybooksPage Component
 *
 * Main page for managing trading strategies and playbooks.
 * Desktop: Stacked master-detail layout with collapsible strategy section.
 * Mobile: Nested page navigation.
 *
 * State Management:
 * - Data state (strategies, playbooks, selection, dirty tracking) → StrategiesPlaybooksContext
 * - UI state (collapse, popovers, modals) → Local component state
 */

// 1. External dependencies
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams, Routes, Route } from 'react-router-dom';
import { Plus, AlertTriangle, Eye, EyeOff } from 'lucide-react';

// 2. Internal absolute imports
import {
  Alert,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  EmptyState,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectItem,
  SelectSection,
  useDisclosure,
} from '@enfomentis/ui';
import { useIsDesktop } from '@/hooks';
import { ResizablePane } from '@/components/shared/ResizablePane';

// 3. Types
import type { Strategy } from '@/types/schema/Strategy';

// 4. Local imports
import { StrategyModal } from './StrategyModal';
import { StrategyRenameModal } from './StrategyRenameModal';
import { PlaybookModal } from './PlaybookModal';
import { PlaybookRenameModal } from './PlaybookRenameModal';
import { LinkPlaybookModal } from './LinkPlaybookModal';
import { StrategyPickerModal } from './StrategyPickerModal';
import { StrategiesPlaybooksProvider, useStrategiesPlaybooks } from './context';
import { ConfluenceLinkingContent, CriteriaLinkingContent, EntryModelLinkingContent, StrategyDetailForm, PlaybookDetailForm, PlaybookListItem, PlaybookLinkageTabs, MoveWithUnsavedChangesDialog, LastPlaybookWarningDialog } from './components';
import {
  MobileStrategyList,
  MobileStrategyCreate,
  MobileStrategyDetail,
  MobilePlaybookDetail,
  MobilePlaybookCreate,
  MobileLinkPlaybook,
} from './pages';

/**
 * Special keys for action items in Select dropdown
 */
const NEW_STRATEGY_KEY = '__new_strategy__';
const TOGGLE_ARCHIVED_KEY = '__toggle_archived__';

/**
 * Inner component that consumes the context.
 * Separated to allow the provider to wrap at the appropriate level.
 */
const StrategiesPlaybooksPageContent: React.FC = () => {
  // Desktop detection
  const isDesktop = useIsDesktop();

  // ===========================================================================
  // Context Consumption
  // ===========================================================================

  const {
    // Hydration state
    isHydrating,

    // Strategy data
    strategies,
    selectedStrategy,
    selectedStrategyId,
    filteredStrategies,
    activeStrategies,
    archivedStrategies,
    loading,
    loadError,
    showArchived,

    // Strategy dirty state
    localDescription,
    localPhilosophy,
    isDirty,
    isSaving,
    showConflictBanner,

    // Strategy actions
    selectStrategy,
    setShowArchived,
    setLocalDescription,
    setLocalPhilosophy,
    saveStrategy,
    discardStrategyChanges,
    archiveStrategy,
    restoreStrategy,
    acceptExternalChanges,
    keepEditing,
    reloadStrategies,

    // Playbook data
    playbooks,
    selectedPlaybook,
    selectedPlaybookId,
    filteredPlaybooks,
    playbooksLoading,
    playbooksLoadError,
    showArchivedPlaybooks,

    // Playbook dirty state
    localPlaybookDescription,
    localPlaybookPhilosophy,
    isPlaybookDirty,
    isPlaybookSaving,
    showPlaybookConflictBanner,

    // Playbook actions
    selectPlaybook,
    setShowArchivedPlaybooks,
    setLocalPlaybookDescription,
    setLocalPlaybookPhilosophy,
    savePlaybook,
    discardPlaybookChanges,
    unlinkPlaybook,
    archivePlaybookFromList,
    restorePlaybookFromList,
    acceptPlaybookExternalChanges,
    keepPlaybookEditing,
    reloadPlaybooks,
    linkPlaybooks,
    movePlaybook,
    copyPlaybook,
    sharePlaybook,

    // Linkage tab state
    activeLinkageTab,
    setActiveLinkageTab,

    // Playbook details collapse state
    isPlaybookDetailsExpanded,
    setIsPlaybookDetailsExpanded,
  } = useStrategiesPlaybooks();

  // ===========================================================================
  // Computed: Playbook Share Info
  // ===========================================================================

  /**
   * Map of playbookId -> array of strategy names that contain the playbook.
   * Only includes active, non-tombstoned strategies.
   */
  const playbookShareMap = useMemo(() => {
    const map = new Map<string, string[]>();

    // Filter to active, non-tombstoned strategies
    const activeStrategiesForSharing = strategies.filter(
      (s) => s.status === 'active' && !s.deletedAt
    );

    // For each playbook in each strategy, track which strategies contain it
    for (const strategy of activeStrategiesForSharing) {
      for (const playbookId of strategy.playbookIds || []) {
        const existing = map.get(playbookId) || [];
        existing.push(strategy.name);
        map.set(playbookId, existing);
      }
    }

    return map;
  }, [strategies]);

  // ===========================================================================
  // UI-Only State (not in context)
  // ===========================================================================

  // Strategy section collapse/expand state
  const [isStrategyExpanded, setIsStrategyExpanded] = useState(false);

  // Controlled Select state (prevents close when toggle is clicked)
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const toggleClickedRef = useRef(false);

  // Track which playbook's actions popover is open (null = none)
  const [playbookActionsPopoverId, setPlaybookActionsPopoverId] = useState<string | null>(null);

  // Popover state for "+ Add" button
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);

  // ===========================================================================
  // Modal State
  // ===========================================================================

  const strategyModalDisclosure = useDisclosure();
  const renameModalDisclosure = useDisclosure();
  const playbookModalDisclosure = useDisclosure();
  const playbookRenameModalDisclosure = useDisclosure();
  const linkPlaybookModalDisclosure = useDisclosure();

  // Move playbook modal state
  const movePickerDisclosure = useDisclosure();
  const moveUnsavedDisclosure = useDisclosure();
  const lastPlaybookWarningDisclosure = useDisclosure();
  const [pendingMovePlaybookId, setPendingMovePlaybookId] = useState<string | null>(null);
  const [pendingMoveTargetId, setPendingMoveTargetId] = useState<string | null>(null);

  // Copy playbook modal state
  const copyPickerDisclosure = useDisclosure();
  const [pendingCopyPlaybookId, setPendingCopyPlaybookId] = useState<string | null>(null);

  // Share playbook modal state
  const sharePickerDisclosure = useDisclosure();
  const [pendingSharePlaybookId, setPendingSharePlaybookId] = useState<string | null>(null);

  // ===========================================================================
  // Force Expand Strategy Section When Empty
  // ===========================================================================

  // Force expand when no strategies exist
  React.useEffect(() => {
    if (!loading && strategies.length === 0) {
      setIsStrategyExpanded(true);
    }
  }, [loading, strategies.length]);

  // ===========================================================================
  // URL Sync (Desktop Only) - Push selection to URL
  // ===========================================================================

  const [searchParams, setSearchParams] = useSearchParams();

  // Push selection state to URL after hydration completes
  useEffect(() => {
    // Guard: only run after hydration, on desktop
    if (isHydrating || !isDesktop) return;

    // Build new params based on current selection
    const newParams = new URLSearchParams();
    if (selectedStrategyId) {
      newParams.set('strategy', selectedStrategyId);
    }
    if (selectedPlaybookId) {
      newParams.set('playbook', selectedPlaybookId);
    }

    // Compare with current URL params to avoid redundant replaceState
    const currentStrategyParam = searchParams.get('strategy');
    const currentPlaybookParam = searchParams.get('playbook');
    const newStrategyParam = newParams.get('strategy');
    const newPlaybookParam = newParams.get('playbook');

    if (
      currentStrategyParam !== newStrategyParam ||
      currentPlaybookParam !== newPlaybookParam
    ) {
      setSearchParams(newParams, { replace: true });
    }
  }, [isHydrating, isDesktop, selectedStrategyId, selectedPlaybookId, searchParams, setSearchParams]);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleShowArchivedToggle = useCallback(
    (checked: boolean) => {
      setShowArchived(checked);
    },
    [setShowArchived]
  );

  const handleStrategySelect = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') return;
      const selected = Array.from(keys)[0] as string | undefined;
      if (!selected) return;

      // Handle "New Strategy" action
      if (selected === NEW_STRATEGY_KEY) {
        strategyModalDisclosure.onOpen();
        return;
      }

      // Handle "Show Archived" toggle action
      if (selected === TOGGLE_ARCHIVED_KEY) {
        toggleClickedRef.current = true;
        handleShowArchivedToggle(!showArchived);
        return;
      }

      // Normal strategy selection
      if (selected !== selectedStrategyId) {
        selectStrategy(selected);
      }
    },
    [selectedStrategyId, showArchived, handleShowArchivedToggle, strategyModalDisclosure, selectStrategy]
  );

  const handleSelectOpenChange = useCallback((open: boolean) => {
    if (!open && toggleClickedRef.current) {
      // Toggle was clicked - reset ref and keep Select open
      toggleClickedRef.current = false;
      return;
    }
    setIsSelectOpen(open);
  }, []);

  const handlePlaybookSelect = useCallback(
    (playbookId: string) => {
      selectPlaybook(playbookId);
    },
    [selectPlaybook]
  );

  const handleShowArchivedPlaybooksToggle = useCallback(() => {
    setShowArchivedPlaybooks(!showArchivedPlaybooks);
  }, [showArchivedPlaybooks, setShowArchivedPlaybooks]);

  const handleStrategyModalSave = useCallback(
    async (newStrategyId?: string) => {
      await reloadStrategies();
      if (newStrategyId) {
        selectStrategy(newStrategyId);
      }
    },
    [reloadStrategies, selectStrategy]
  );

  const handleRenameModalSave = useCallback(async () => {
    await reloadStrategies();
  }, [reloadStrategies]);

  const handlePlaybookModalSave = useCallback(
    async (newPlaybookId?: string) => {
      // Reload strategies to get updated playbookIds
      await reloadStrategies();
      // Reload playbooks
      await reloadPlaybooks();
      // Select the new playbook
      if (newPlaybookId) {
        selectPlaybook(newPlaybookId);
      }
    },
    [reloadStrategies, reloadPlaybooks, selectPlaybook]
  );

  const handlePlaybookRenameModalSave = useCallback(async () => {
    await reloadPlaybooks();
  }, [reloadPlaybooks]);

  const handlePlaybookUnlink = useCallback(
    async (playbookId: string) => {
      await unlinkPlaybook(playbookId);
      setPlaybookActionsPopoverId(null);
    },
    [unlinkPlaybook]
  );

  const handlePlaybookArchiveFromList = useCallback(
    async (playbookId: string) => {
      await archivePlaybookFromList(playbookId);
      setPlaybookActionsPopoverId(null);
    },
    [archivePlaybookFromList]
  );

  const handlePlaybookRestoreFromList = useCallback(
    async (playbookId: string) => {
      await restorePlaybookFromList(playbookId);
      setPlaybookActionsPopoverId(null);
    },
    [restorePlaybookFromList]
  );

  /**
   * Handle linking existing playbooks to the current strategy
   */
  const handleLinkPlaybooks = useCallback(
    async (playbookIds: string[]) => {
      await linkPlaybooks(playbookIds);
    },
    [linkPlaybooks]
  );

  /**
   * Handle "Create New Playbook" from LinkPlaybookModal empty state
   */
  const handleLinkModalCreateNew = useCallback(() => {
    linkPlaybookModalDisclosure.onClose();
    playbookModalDisclosure.onOpen();
  }, [linkPlaybookModalDisclosure, playbookModalDisclosure]);

  /**
   * Handle popover option: Create New
   */
  const handleAddPopoverCreateNew = useCallback(() => {
    setIsAddPopoverOpen(false);
    playbookModalDisclosure.onOpen();
  }, [playbookModalDisclosure]);

  /**
   * Handle popover option: Add Existing
   */
  const handleAddPopoverAddExisting = useCallback(() => {
    setIsAddPopoverOpen(false);
    linkPlaybookModalDisclosure.onOpen();
  }, [linkPlaybookModalDisclosure]);

  // ===========================================================================
  // Move Playbook Handlers
  // ===========================================================================

  /**
   * Step 1: User clicks "Move to Strategy" in popover
   * - If dirty, show unsaved changes dialog
   * - If not dirty, open strategy picker
   */
  const handleMoveClick = useCallback(
    (playbookId: string) => {
      setPendingMovePlaybookId(playbookId);
      setPlaybookActionsPopoverId(null); // Close actions popover

      if (isPlaybookDirty) {
        moveUnsavedDisclosure.onOpen();
      } else {
        movePickerDisclosure.onOpen();
      }
    },
    [isPlaybookDirty, moveUnsavedDisclosure, movePickerDisclosure]
  );

  /**
   * Step 2a: User chose "Save & Move" from unsaved dialog
   */
  const handleSaveAndMove = useCallback(async () => {
    await savePlaybook();
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onOpen();
  }, [savePlaybook, moveUnsavedDisclosure, movePickerDisclosure]);

  /**
   * Step 2b: User chose "Discard & Move" from unsaved dialog
   */
  const handleDiscardAndMove = useCallback(() => {
    discardPlaybookChanges();
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onOpen();
  }, [discardPlaybookChanges, moveUnsavedDisclosure, movePickerDisclosure]);

  /**
   * Step 4: Execute the move operation
   * (Defined before handleMovePickerConfirm to satisfy dependency order)
   */
  const handleExecuteMove = useCallback(
    async (targetId: string) => {
      if (!pendingMovePlaybookId) return;

      await movePlaybook(pendingMovePlaybookId, targetId);

      // Reset state
      setPendingMovePlaybookId(null);
      setPendingMoveTargetId(null);
      lastPlaybookWarningDisclosure.onClose();
    },
    [pendingMovePlaybookId, movePlaybook, lastPlaybookWarningDisclosure]
  );

  /**
   * Step 3: User selected target strategy from picker
   * - If last playbook in strategy, show warning
   * - Otherwise, execute move
   */
  const handleMovePickerConfirm = useCallback(
    (strategyIds: string[]) => {
      const targetId = strategyIds[0];
      if (!targetId) return;

      setPendingMoveTargetId(targetId);
      movePickerDisclosure.onClose();

      // Check if this is the last playbook in the strategy
      const strategyPlaybookCount = selectedStrategy?.playbookIds?.length ?? 0;
      if (strategyPlaybookCount === 1) {
        lastPlaybookWarningDisclosure.onOpen();
      } else {
        // Execute move immediately
        handleExecuteMove(targetId);
      }
    },
    [selectedStrategy, movePickerDisclosure, lastPlaybookWarningDisclosure, handleExecuteMove]
  );

  /**
   * Step 4 (alternate): User confirmed last-playbook warning
   */
  const handleLastPlaybookWarningConfirm = useCallback(() => {
    if (pendingMoveTargetId) {
      handleExecuteMove(pendingMoveTargetId);
    }
  }, [pendingMoveTargetId, handleExecuteMove]);

  /**
   * Cancel move operation (from any dialog)
   */
  const handleMoveCancel = useCallback(() => {
    setPendingMovePlaybookId(null);
    setPendingMoveTargetId(null);
    moveUnsavedDisclosure.onClose();
    movePickerDisclosure.onClose();
    lastPlaybookWarningDisclosure.onClose();
  }, [moveUnsavedDisclosure, movePickerDisclosure, lastPlaybookWarningDisclosure]);

  // ===========================================================================
  // Copy Playbook Handlers
  // ===========================================================================

  /**
   * Handle "Duplicate" click - copy within current strategy
   */
  const handleDuplicateClick = useCallback(
    async (playbookId: string) => {
      if (!selectedStrategyId) return;
      setPlaybookActionsPopoverId(null); // Close actions popover
      await copyPlaybook(playbookId, selectedStrategyId);
    },
    [selectedStrategyId, copyPlaybook]
  );

  /**
   * Handle "Copy to Strategy..." click - open strategy picker
   */
  const handleCopyToStrategyClick = useCallback(
    (playbookId: string) => {
      setPendingCopyPlaybookId(playbookId);
      setPlaybookActionsPopoverId(null); // Close actions popover
      copyPickerDisclosure.onOpen();
    },
    [copyPickerDisclosure]
  );

  /**
   * Handle strategy picker confirmation for copy
   */
  const handleCopyPickerConfirm = useCallback(
    async (strategyIds: string[]) => {
      const targetId = strategyIds[0];
      if (!targetId || !pendingCopyPlaybookId) return;

      copyPickerDisclosure.onClose();
      await copyPlaybook(pendingCopyPlaybookId, targetId);
      setPendingCopyPlaybookId(null);
    },
    [pendingCopyPlaybookId, copyPlaybook, copyPickerDisclosure]
  );

  /**
   * Cancel copy operation
   */
  const handleCopyCancel = useCallback(() => {
    setPendingCopyPlaybookId(null);
    copyPickerDisclosure.onClose();
  }, [copyPickerDisclosure]);

  // ===========================================================================
  // Share Playbook Handlers
  // ===========================================================================

  /**
   * Handle "Share with Strategy..." click - open strategy picker
   */
  const handleShareClick = useCallback(
    (playbookId: string) => {
      setPendingSharePlaybookId(playbookId);
      setPlaybookActionsPopoverId(null); // Close actions popover
      sharePickerDisclosure.onOpen();
    },
    [sharePickerDisclosure]
  );

  /**
   * Handle strategy picker confirmation for share
   */
  const handleSharePickerConfirm = useCallback(
    async (strategyIds: string[]) => {
      if (!pendingSharePlaybookId || strategyIds.length === 0) return;

      sharePickerDisclosure.onClose();
      await sharePlaybook(pendingSharePlaybookId, strategyIds);
      setPendingSharePlaybookId(null);
    },
    [pendingSharePlaybookId, sharePlaybook, sharePickerDisclosure]
  );

  /**
   * Cancel share operation
   */
  const handleShareCancel = useCallback(() => {
    setPendingSharePlaybookId(null);
    sharePickerDisclosure.onClose();
  }, [sharePickerDisclosure]);

  // ===========================================================================
  // Render Helpers
  // ===========================================================================

  // Format strategy name with playbook count
  const formatStrategyLabel = (strategy: Strategy) => {
    return strategy.name;
  };

  // ===========================================================================
  // Render
  // ===========================================================================

  // Desktop layout
  if (isDesktop) {
    const hasStrategies = strategies.length > 0;
    const hasFilteredStrategies = filteredStrategies.length > 0;

    return (
      <div className="flex flex-col gap-6 w-full h-full">
        {/* Strategy Section */}
        <Card shadow="sm" radius="sm">
          <CardHeader className="flex flex-row items-center gap-2">
            {/* Strategy Select with label (replaces heading) */}
            {hasStrategies && (
              <Select
                label="Strategy:"
                aria-label="Select strategy"
                placeholder="Select strategy"
                selectedKeys={selectedStrategyId ? [selectedStrategyId] : []}
                onSelectionChange={handleStrategySelect}
                isOpen={isSelectOpen}
                onOpenChange={handleSelectOpenChange}
                className="flex w-full"
                isDisabled={loading}
                labelPlacement="outside-left"
                classNames={{
                  label: 'text-lg font-semibold text-text-primary',
                  trigger: 'bg-transparent shadow-none data-[hover=true]:bg-default-50',
                  value: '!text-text-primary text-lg',
                }}
                listboxProps={{
                  itemClasses: {
                    base: 'data-[hover=true]:!text-primary data-[hover=true]:!bg-transparent',
                  },
                }}
              >
                {[
                  // Active strategies section (showDivider: toggle section always follows)
                  ...(activeStrategies.length > 0
                    ? [
                        <SelectSection key="active" title="Active" showDivider>
                          {activeStrategies.map((strategy) => (
                            <SelectItem key={strategy.id}>
                              {formatStrategyLabel(strategy)}
                            </SelectItem>
                          ))}
                        </SelectSection>,
                      ]
                    : []),
                  // Actions section (New + Show Archived toggle)
                  <SelectSection
                    key="__actions_section__"
                    showDivider={showArchived && archivedStrategies.length > 0}
                  >
                    <SelectItem
                      key={NEW_STRATEGY_KEY}
                      startContent={<Plus size={16} />}
                      className="text-secondary"
                    >
                      New Strategy
                    </SelectItem>
                    <SelectItem
                      key={TOGGLE_ARCHIVED_KEY}
                      startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                    >
                      Archived
                    </SelectItem>
                  </SelectSection>,
                  // Archived strategies section (only when showArchived is on)
                  ...(showArchived && archivedStrategies.length > 0
                    ? [
                        <SelectSection key="archived" title="Archived">
                          {archivedStrategies.map((strategy) => (
                            <SelectItem key={strategy.id}>
                              {formatStrategyLabel(strategy)}
                            </SelectItem>
                          ))}
                        </SelectSection>,
                      ]
                    : []),
                ]}
              </Select>
            )}

            {/* When no strategies, show heading instead */}
            {!hasStrategies && (
              <span className="text-lg font-semibold text-text-primary">Strategies</span>
            )}

            <div className="flex items-center gap-2 ml-auto">
              {/* Rename Button */}
              <Button
                size="sm"
                variant="light"
                onPress={renameModalDisclosure.onOpen}
                isDisabled={!selectedStrategy || loading}
              >
                Rename
              </Button>
              {/* Collapse/Expand Toggle */}
              <Button
                size="sm"
                variant="light"
                onPress={() => setIsStrategyExpanded((prev) => !prev)}
              >
                {isStrategyExpanded ? 'Hide Details' : 'Show Details'}
              </Button>
            </div>
          </CardHeader>

          {/* CardBody - rendered when expanded OR loading/error/empty states need to show */}
          {(isStrategyExpanded ||
            loading ||
            loadError ||
            (!loading && !loadError && (!hasStrategies || !hasFilteredStrategies))) && (
            <CardBody className="space-y-4 border-t border-default-200">
              {/* Load Error Alert - always visible when error */}
              {loadError && !loading && (
                <div className="space-y-2">
                  <Alert color="danger" variant="flat">
                    Failed to load strategies. Please try refreshing the page.
                  </Alert>
                  <Button size="sm" variant="flat" onPress={reloadStrategies}>
                    Retry
                  </Button>
                </div>
              )}

              {/* Empty State - No strategies (always visible) */}
              {!hasStrategies && !loading && !loadError && (
                <EmptyState
                  title="Create your first strategy"
                  description="Strategies help organize your playbooks by trading approach"
                  action={
                    <Button
                      variant="bordered"
                      startContent={<Plus size={16} />}
                      onPress={strategyModalDisclosure.onOpen}
                    >
                      New Strategy
                    </Button>
                  }
                />
              )}

              {/* Empty State - All archived, filter off (always visible) */}
              {hasStrategies && !hasFilteredStrategies && !loading && !loadError && (
                <EmptyState
                  title="No active strategies"
                  description="All strategies are archived. Enable 'Show Archived' to view them."
                />
              )}

              {/* Conflict Banner - only when expanded */}
              {isStrategyExpanded && showConflictBanner && (
                <div className="rounded-lg bg-warning-50 border border-warning-200 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                      <span className="text-sm text-warning-700">
                        Strategy updated in another tab
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 md:flex-row">
                      <Button size="sm" variant="flat" onPress={keepEditing}>
                        Keep editing
                      </Button>
                      <Button size="sm" color="warning" onPress={acceptExternalChanges}>
                        Accept changes
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Strategy Detail Form - only when expanded AND selected */}
              {isStrategyExpanded && selectedStrategy && (
                <StrategyDetailForm
                  description={localDescription}
                  philosophy={localPhilosophy}
                  onDescriptionChange={setLocalDescription}
                  onPhilosophyChange={setLocalPhilosophy}
                />
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-4">
                  <p className="text-text-secondary">Loading strategies...</p>
                </div>
              )}
            </CardBody>
          )}

          {/* CardFooter - only when expanded, always rendered for layout stability */}
          {isStrategyExpanded && (
            <CardFooter className="flex items-center justify-between gap-4 border-t border-default-200">
              <div className="flex items-center gap-3">
                {/* Archive/Restore Button - button text conveys status */}
                {selectedStrategy?.status === 'active' && (
                  <Button
                    variant="flat"
                    size="sm"
                    onPress={archiveStrategy}
                    isDisabled={isSaving || !selectedStrategy}
                  >
                    Archive
                  </Button>
                )}
                {selectedStrategy?.status === 'archived' && (
                  <Button
                    variant="flat"
                    size="sm"
                    onPress={restoreStrategy}
                    isDisabled={isSaving || !selectedStrategy}
                  >
                    Restore
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isDirty && <span className="text-sm text-warning-600">Unsaved changes</span>}
                <Button
                  variant="flat"
                  size="sm"
                  onPress={discardStrategyChanges}
                  isDisabled={!isDirty || isSaving}
                >
                  Discard
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onPress={saveStrategy}
                  isDisabled={!isDirty || isSaving}
                  isLoading={isSaving}
                >
                  {isDirty && <span className="w-2 h-2 rounded-full bg-white mr-1" />}
                  Save
                </Button>
              </div>
            </CardFooter>
          )}
        </Card>

        {/* Playbooks Section */}
        <Card shadow="sm" radius="sm" className="flex-1 min-h-0 flex flex-col">
          <CardHeader>
            <h3 className="text-lg font-semibold text-text-primary">Playbooks</h3>
          </CardHeader>
          <CardBody className="flex-1 min-h-0 p-0 border-t border-default-200">
            {/* No strategy selected - full CardBody empty state */}
            {!selectedStrategy && (
              <div className="p-4">
                <EmptyState
                  title="No strategy selected"
                  description="Select a strategy above to view its playbooks"
                />
              </div>
            )}

            {/* Strategy selected - side-by-side layout with ResizablePane */}
            {selectedStrategy && (
              <ResizablePane
                storageKey="playbooks-list-pane-width"
                defaultWidth={256}
                minWidth={200}
                maxWidth={400}
                leftContent={
                  <Card
                    shadow="none"
                    radius="none"
                    className="flex flex-col h-full"
                  >
                    <CardHeader className="flex items-center justify-between gap-2 px-3 py-2 shrink-0">
                      <div className="flex items-center gap-2">
                        {/* Archived toggle */}
                        {playbooks.length > 0 && (
                          <Button
                            size="sm"
                            variant="light"
                            startContent={
                              showArchivedPlaybooks ? <Eye size={16} /> : <EyeOff size={16} />
                            }
                            onPress={handleShowArchivedPlaybooksToggle}
                            title={showArchivedPlaybooks ? 'Hide Archived' : 'Show Archived'}
                            aria-label={showArchivedPlaybooks ? 'Hide Archived' : 'Show Archived'}
                            className={
                              showArchivedPlaybooks ? 'text-text-primary' : 'text-text-tertiary'
                            }
                          >
                            Archived
                          </Button>
                        )}
                        {/* Add button with popover */}
                        <Popover
                          isOpen={isAddPopoverOpen}
                          onOpenChange={setIsAddPopoverOpen}
                          placement="bottom-start"
                        >
                          <PopoverTrigger>
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              title="Add Playbook"
                              aria-label="Add Playbook"
                            >
                              <Plus size={16} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-1">
                            <div className="flex flex-col min-w-40">
                              <Button
                                size="md"
                                variant="light"
                                className="justify-start"
                                onPress={handleAddPopoverCreateNew}
                              >
                                Create New...
                              </Button>
                              <Button
                                size="md"
                                variant="light"
                                className="justify-start"
                                onPress={handleAddPopoverAddExisting}
                              >
                                Add Existing...
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </CardHeader>

                  <CardBody className="flex-1 min-h-0 overflow-y-auto p-2">
                    {/* Loading state */}
                    {playbooksLoading && (
                      <div className="text-center py-4">
                        <p className="text-text-secondary text-sm">Loading...</p>
                      </div>
                    )}

                    {/* Load error */}
                    {playbooksLoadError && !playbooksLoading && (
                      <div className="space-y-2 p-2">
                        <Alert color="danger" variant="flat">
                          Failed to load playbooks.
                        </Alert>
                        <Button size="sm" variant="flat" onPress={reloadPlaybooks}>
                          Retry
                        </Button>
                      </div>
                    )}

                    {/* No playbooks yet */}
                    {!playbooksLoading && !playbooksLoadError && playbooks.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-text-secondary text-sm">No playbooks yet</p>
                        <Button
                          size="sm"
                          variant="flat"
                          startContent={<Plus size={14} />}
                          onPress={playbookModalDisclosure.onOpen}
                          className="mt-2"
                        >
                          Add Playbook
                        </Button>
                      </div>
                    )}

                    {/* All archived, filter off */}
                    {!playbooksLoading &&
                      !playbooksLoadError &&
                      playbooks.length > 0 &&
                      filteredPlaybooks.length === 0 && (
                        <div className="text-center py-4">
                          <p className="text-text-secondary text-sm">All playbooks archived</p>
                          <Button
                            size="sm"
                            variant="light"
                            startContent={<Eye size={14} />}
                            onPress={handleShowArchivedPlaybooksToggle}
                            className="mt-2"
                          >
                            Show Archived
                          </Button>
                        </div>
                      )}

                    {/* Playbook List */}
                    {!playbooksLoading && !playbooksLoadError && filteredPlaybooks.length > 0 && (
                      <div className="space-y-1">
                        {filteredPlaybooks.map((playbook) => (
                          <PlaybookListItem
                            key={playbook.id}
                            playbook={playbook}
                            isSelected={selectedPlaybookId === playbook.id}
                            isArchived={playbook.status === 'archived'}
                            onSelect={() => handlePlaybookSelect(playbook.id)}
                            sharedStrategyNames={playbookShareMap.get(playbook.id)}
                            onDuplicate={() => handleDuplicateClick(playbook.id)}
                            onCopyToStrategy={() => handleCopyToStrategyClick(playbook.id)}
                            onMove={() => handleMoveClick(playbook.id)}
                            onShare={() => handleShareClick(playbook.id)}
                            onUnlink={() => handlePlaybookUnlink(playbook.id)}
                            onArchive={
                              playbook.status !== 'archived'
                                ? () => handlePlaybookArchiveFromList(playbook.id)
                                : undefined
                            }
                            onRestore={
                              playbook.status === 'archived'
                                ? () => handlePlaybookRestoreFromList(playbook.id)
                                : undefined
                            }
                            isActionsDisabled={isPlaybookSaving}
                            showActionsPopover={playbookActionsPopoverId === playbook.id}
                            onActionsPopoverChange={(open) =>
                              setPlaybookActionsPopoverId(open ? playbook.id : null)
                            }
                          />
                        ))}
                      </div>
                    )}
                    </CardBody>
                  </Card>
                }
                rightContent={
                  <Card shadow="none" radius="none" className="h-full flex flex-col min-w-0">
                    <CardHeader className="flex items-center justify-between gap-2 px-3 py-2 shrink-0">
                      <div className="flex items-center gap-2 min-w-0">
                        {/* Playbook name */}
                        {selectedPlaybook ? (
                        <h4 className="font-semibold text-text-primary truncate">
                          {selectedPlaybook.name}
                        </h4>
                      ) : (
                        <span className="text-text-secondary">Select a playbook</span>
                      )}
                    </div>
                    {/* Rename button */}
                    {selectedPlaybook && (
                      <Button
                        size="sm"
                        variant="light"
                        onPress={playbookRenameModalDisclosure.onOpen}
                      >
                        Rename
                      </Button>
                    )}
                  </CardHeader>

                  <CardBody className="flex-1 min-h-0 flex flex-col p-4">
                    {/* Empty state - no playbook selected */}
                    {!selectedPlaybook && filteredPlaybooks.length > 0 && (
                      <EmptyState
                        title="Select a playbook"
                        description="Choose a playbook from the list to view and edit its details"
                      />
                    )}

                    {/* Empty state - no playbooks exist */}
                    {!selectedPlaybook &&
                      filteredPlaybooks.length === 0 &&
                      playbooks.length === 0 &&
                      !playbooksLoading && (
                        <EmptyState
                          title="Create your first playbook"
                          description={`Add a playbook to "${selectedStrategy.name}" to get started`}
                          action={
                            <Button
                              variant="flat"
                              startContent={<Plus size={16} />}
                              onPress={playbookModalDisclosure.onOpen}
                            >
                              Add Playbook
                            </Button>
                          }
                        />
                      )}

                    {/* Playbook detail form */}
                    {selectedPlaybook && (
                      <div className="flex flex-col gap-4 h-full min-h-0">
                        {/* Conflict Banner */}
                        {showPlaybookConflictBanner && (
                          <div className="rounded-lg bg-warning-50 border border-warning-200 p-4 shrink-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                                <span className="text-sm text-warning-700">
                                  Playbook updated in another tab
                                </span>
                              </div>
                              <div className="flex flex-col gap-2 md:flex-row">
                                <Button size="sm" variant="flat" onPress={keepPlaybookEditing}>
                                  Keep editing
                                </Button>
                                <Button
                                  size="sm"
                                  color="warning"
                                  onPress={acceptPlaybookExternalChanges}
                                >
                                  Accept changes
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Collapsible Playbook Details Card */}
                        <Card shadow="none" radius="sm" className="border border-default-200 shrink-0">
                          <CardHeader className="flex items-center justify-between px-3 py-2">
                            <span className="text-sm text-text-primary">Description & Philosophy</span>
                            <Button
                              size="sm"
                              variant="light"
                              onPress={() => setIsPlaybookDetailsExpanded(!isPlaybookDetailsExpanded)}
                            >
                              {isPlaybookDetailsExpanded ? 'Hide Details' : 'Show Details'}
                            </Button>
                          </CardHeader>
                          {isPlaybookDetailsExpanded && (
                            <CardBody className="border-t border-default-200 p-3">
                              <PlaybookDetailForm
                                description={localPlaybookDescription}
                                philosophy={localPlaybookPhilosophy}
                                onDescriptionChange={setLocalPlaybookDescription}
                                onPhilosophyChange={setLocalPlaybookPhilosophy}
                              />
                            </CardBody>
                          )}
                        </Card>

                        {/* Playbook Linkage Tabs */}
                        <PlaybookLinkageTabs
                          activeTab={activeLinkageTab}
                          onTabChange={setActiveLinkageTab}
                          criteriaContent={<CriteriaLinkingContent />}
                          confluencesContent={<ConfluenceLinkingContent />}
                          entryModelsContent={<EntryModelLinkingContent />}
                        />
                      </div>
                    )}
                  </CardBody>

                  {/* Detail Panel Footer */}
                  {selectedPlaybook && (
                    <CardFooter className="flex items-center justify-between gap-4 px-3 py-2 border-t border-default-200 shrink-0">
                      <div className="flex items-center gap-3">
                        {/* Archive/Restore actions moved to list row popover */}
                      </div>
                      <div className="flex items-center gap-2">
                        {isPlaybookDirty && (
                          <span className="text-sm text-warning-600">Unsaved changes</span>
                        )}
                        <Button
                          variant="flat"
                          size="sm"
                          onPress={discardPlaybookChanges}
                          isDisabled={!isPlaybookDirty || isPlaybookSaving}
                        >
                          Discard
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          onPress={savePlaybook}
                          isDisabled={!isPlaybookDirty || isPlaybookSaving}
                          isLoading={isPlaybookSaving}
                        >
                          {isPlaybookDirty && <span className="w-2 h-2 rounded-full bg-white mr-1" />}
                          Save
                        </Button>
                      </div>
                      </CardFooter>
                    )}
                  </Card>
                }
              />
            )}
          </CardBody>
        </Card>

        {/* Modals */}
        <StrategyModal
          isOpen={strategyModalDisclosure.isOpen}
          onClose={strategyModalDisclosure.onClose}
          onSave={handleStrategyModalSave}
        />

        {selectedStrategy && (
          <StrategyRenameModal
            isOpen={renameModalDisclosure.isOpen}
            onClose={renameModalDisclosure.onClose}
            strategy={selectedStrategy}
            onSave={handleRenameModalSave}
          />
        )}

        {selectedStrategy && (
          <PlaybookModal
            isOpen={playbookModalDisclosure.isOpen}
            onClose={playbookModalDisclosure.onClose}
            onSave={handlePlaybookModalSave}
            strategyId={selectedStrategy.id}
            strategyName={selectedStrategy.name}
          />
        )}

        {selectedPlaybook && (
          <PlaybookRenameModal
            isOpen={playbookRenameModalDisclosure.isOpen}
            onClose={playbookRenameModalDisclosure.onClose}
            playbook={selectedPlaybook}
            onSave={handlePlaybookRenameModalSave}
          />
        )}

        {selectedStrategy && (
          <LinkPlaybookModal
            isOpen={linkPlaybookModalDisclosure.isOpen}
            onClose={linkPlaybookModalDisclosure.onClose}
            onLink={handleLinkPlaybooks}
            onCreateNew={handleLinkModalCreateNew}
            strategyName={selectedStrategy.name}
            currentPlaybookIds={selectedStrategy.playbookIds || []}
          />
        )}

        {/* Move Playbook Modals */}
        <MoveWithUnsavedChangesDialog
          isOpen={moveUnsavedDisclosure.isOpen}
          onClose={handleMoveCancel}
          onSaveAndMove={handleSaveAndMove}
          onDiscardAndMove={handleDiscardAndMove}
          isSaving={isPlaybookSaving}
        />

        {selectedStrategy && pendingMovePlaybookId && (
          <StrategyPickerModal
            isOpen={movePickerDisclosure.isOpen}
            onClose={handleMoveCancel}
            onConfirm={handleMovePickerConfirm}
            mode="move"
            playbookId={pendingMovePlaybookId}
            currentStrategyId={selectedStrategy.id}
            playbookName={
              playbooks.find((p) => p.id === pendingMovePlaybookId)?.name || 'Playbook'
            }
          />
        )}

        {selectedStrategy && (
          <LastPlaybookWarningDialog
            isOpen={lastPlaybookWarningDisclosure.isOpen}
            onClose={handleMoveCancel}
            onConfirm={handleLastPlaybookWarningConfirm}
            strategyName={selectedStrategy.name}
          />
        )}

        {/* Copy Playbook Modal */}
        {selectedStrategy && pendingCopyPlaybookId && (
          <StrategyPickerModal
            isOpen={copyPickerDisclosure.isOpen}
            onClose={handleCopyCancel}
            onConfirm={handleCopyPickerConfirm}
            mode="copy"
            playbookId={pendingCopyPlaybookId}
            currentStrategyId={selectedStrategy.id}
            playbookName={
              playbooks.find((p) => p.id === pendingCopyPlaybookId)?.name || 'Playbook'
            }
          />
        )}

        {/* Share Playbook Modal */}
        {selectedStrategy && pendingSharePlaybookId && (
          <StrategyPickerModal
            isOpen={sharePickerDisclosure.isOpen}
            onClose={handleShareCancel}
            onConfirm={handleSharePickerConfirm}
            mode="share"
            playbookId={pendingSharePlaybookId}
            currentStrategyId={selectedStrategy.id}
            playbookName={
              playbooks.find((p) => p.id === pendingSharePlaybookId)?.name || 'Playbook'
            }
          />
        )}
      </div>
    );
  }

  // Mobile layout - nested page navigation
  return (
    <Routes>
      <Route index element={<MobileStrategyList />} />
      <Route path="new" element={<MobileStrategyCreate />} />
      <Route path=":strategyId" element={<MobileStrategyDetail />} />
      {/* New playbook routes - must come before :playbookId wildcard */}
      <Route path=":strategyId/playbooks/new" element={<MobilePlaybookCreate />} />
      <Route path=":strategyId/playbooks/link" element={<MobileLinkPlaybook />} />
      <Route path=":strategyId/playbooks/:playbookId" element={<MobilePlaybookDetail />} />
    </Routes>
  );
};

/**
 * Strategies & Playbooks Page
 *
 * Wraps the page content with the StrategiesPlaybooksProvider.
 */
export const StrategiesPlaybooksPage: React.FC = () => {
  return (
    <StrategiesPlaybooksProvider>
      <StrategiesPlaybooksPageContent />
    </StrategiesPlaybooksProvider>
  );
};
